'use client'

import { useEffect, useState } from 'react'
import { Users } from 'lucide-react'

const STORAGE_KEY = 'nc_visitor_id'

function getOrCreateMachineId(): string | null {
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY)
    if (existing) return existing
    const id = crypto.randomUUID ? crypto.randomUUID() : fallbackUuid()
    window.localStorage.setItem(STORAGE_KEY, id)
    return id
  } catch {
    return null
  }
}

function fallbackUuid() {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

async function getFingerprint(): Promise<string | null> {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 240
    canvas.height = 60
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillStyle = '#f60'
    ctx.fillRect(2, 2, 236, 56)
    ctx.fillStyle = '#069'
    ctx.fillText('NewEmail visitor \u2713 0123456789', 8, 18)
    const rendered = canvas.toDataURL()
    const signals = [
      navigator.userAgent,
      navigator.language,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      `${screen.width}x${screen.height}x${screen.colorDepth}`,
      navigator.platform || '',
      String(navigator.hardwareConcurrency || ''),
      typeof (navigator as Navigator & { deviceMemory?: number }).deviceMemory === 'number'
        ? String((navigator as Navigator & { deviceMemory?: number }).deviceMemory)
        : '',
      rendered,
    ].join('|')
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(signals))
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return null
  }
}

export function VisitorCounter() {
  const [total, setTotal] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    const report = async () => {
      const id = getOrCreateMachineId()
      const fingerprint = await getFingerprint()
      const res = await fetch('/api/visitor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, fingerprint }),
      })
      const data = res.ok ? await res.json() : null
      if (!cancelled && data && typeof data.total === 'number') setTotal(data.total)
    }
    report().catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  if (total === null) return null

  return (
    <span
      title="Unique visitors"
      className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/70 px-2.5 py-1 font-mono text-xs text-muted-foreground"
    >
      <Users className="h-3.5 w-3.5 text-primary" />
      {total.toLocaleString()} visitors
    </span>
  )
}
